"use client";

import { useState, useEffect, useCallback } from "react";

interface Props {
  token: string;
}

type AdminTab =
  | "stats"
  | "catalog"
  | "bookings"
  | "maintenance"
  | "submissions"
  | "rfid"
  | "groups"
  | "controllers"
  | "donation_settings";

// ─── API helpers ─────────────────────────────────────────────────────────────

async function api(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<unknown> {
  const res = await fetch(`/api/toollib/${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg);
  }
  return res.json();
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatsData {
  tools_total: number;
  tools_available: number;
  tools_lent: number;
  tools_maintenance: number;
  materials_count: number;
  bookings_active: number;
  bookings_overdue: number;
  submissions_pending: number;
  maintenance_scheduled: number;
  rfid_users: number;
  controllers: number;
}

interface Tool {
  uuid: string;
  name: string;
  category: string;
  item_type: string;
  status: string;
  condition: string;
  brand?: string;
  model?: string;
  is_free?: boolean;
  daily_rate: number;
  substitution_group_id?: string;
  storage_locations?: StorageLocation[];
  description?: string;
  requires_training?: boolean;
  subscription_included?: boolean;
  serial_number?: string;
  barcode?: string;
  image_url?: string;
  tags?: string[];
  next_maintenance_date?: string;
}

interface StorageLocation {
  type: string;
  label: string;
  controller_id?: string;
  locker_number?: number;
  shelf_id?: string;
  section?: string;
  address?: string;
  room?: string;
}

interface Booking {
  booking_id: string;
  user_name: string;
  status: string;
  start_date: string;
  end_date: string;
  tool_uuids: string[];
  billing_type: string;
}

interface MaintenanceLog {
  log_id: string;
  tool_uuid: string;
  tool_name: string;
  maintenance_type: string;
  status: string;
  scheduled_date?: string;
  completed_date?: string;
  performed_by?: string;
  parts_used: PartEntry[];
  total_cost: number;
  odoo_invoice_number?: string;
  notes?: string;
}

interface PartEntry {
  part_name: string;
  part_number: string;
  quantity: number;
  unit_price: number;
}

interface Submission {
  submission_id: string;
  submitter_name: string;
  submitter_email: string;
  submitter_phone?: string;
  item_name: string;
  item_type: string;
  category: string;
  condition: string;
  description: string;
  image_url?: string;
  status: string;
  created_at: string;
  admin_notes?: string;
  pickup_preference?: string;  // "pickup" = we fetch | "delivery" = they bring it
  storage_preference?: string; // pickup address when pickup_preference === "pickup"
  pickup_address?: string;
  pickup_description?: string;
  pickup_time_span?: string;
  pickup_instructions?: string;
  pickup_token?: string;
  pickup_qr_b64?: string;
  pickup_url?: string;
  tool_uuid?: string;
  reviewed_at?: string;
  reviewed_by?: string;
}

interface RfidUser {
  user_id: string;
  name: string;
  email: string;
  group_ids: string[];
  access_level: string;
  active: boolean;
}

interface Group {
  group_id: string;
  name: string;
  description: string;
  pricing_scheme?: string;   // "per_tool_daily" | "fixed_all"
  fixed_price?: number;
  odoo_partner_id?: number | null;
  day_passwords?: { date: string; plain_password?: string; allowed_controllers?: string[]; allowed_lockers?: number[] }[];
}

interface Controller {
  controller_id: string;
  ip_address: string;
  location: string;
  locker_count: number;
  status?: string;
}

interface SubGroup {
  group_id: string;
  name: string;
  category: string;
  description: string;
  tool_count: number;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    available: "bg-green-100 text-green-800",
    lent: "bg-blue-100 text-blue-800",
    maintenance: "bg-yellow-100 text-yellow-800",
    lost: "bg-red-100 text-red-800",
    active: "bg-green-100 text-green-800",
    overdue: "bg-red-100 text-red-800",
    returned: "bg-gray-100 text-gray-700",
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    scheduled: "bg-blue-100 text-blue-800",
    in_progress: "bg-orange-100 text-orange-800",
    completed: "bg-green-100 text-green-800",
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[status] || "bg-gray-100 text-gray-700"}`}
    >
      {status}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ToolLibraryAdmin({ token }: Props) {
  const [tab, setTab] = useState<AdminTab>("stats");
  const [error, setError] = useState<string>("");

  const tabs: { id: AdminTab; label: string }[] = [
    { id: "stats", label: "📊 Oversigt" },
    { id: "catalog", label: "🔧 Katalog" },
    { id: "bookings", label: "📋 Bookinger" },
    { id: "maintenance", label: "🔨 Vedligehold" },
    { id: "submissions", label: "📬 Indsendelser" },
    { id: "rfid", label: "📟 RFID" },
    { id: "groups", label: "👥 Grupper" },
    { id: "controllers", label: "🔌 Controllere" },
    { id: "donation_settings", label: "⚙️ Aflevering & Afhentning" },
  ];

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error} <button onClick={() => setError("")} className="ml-2 font-bold">✕</button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "stats" && <StatsTab token={token} setError={setError} />}
      {tab === "catalog" && <CatalogTab token={token} setError={setError} />}
      {tab === "bookings" && <BookingsTab token={token} setError={setError} />}
      {tab === "maintenance" && <MaintenanceTab token={token} setError={setError} />}
      {tab === "submissions" && <SubmissionsTab token={token} setError={setError} setTab={setTab} />}
      {tab === "rfid" && <RfidTab token={token} setError={setError} />}
      {tab === "groups" && <GroupsTab token={token} setError={setError} />}
      {tab === "controllers" && <ControllersTab token={token} setError={setError} />}
      {tab === "donation_settings" && <DonationSettingsTab token={token} setError={setError} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stats tab
// ─────────────────────────────────────────────────────────────────────────────

function StatsTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("admin/stats", token)
      .then((d) => setStats(d as StatsData))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  if (loading) return <p className="text-gray-500 text-sm">Indlæser...</p>;
  if (!stats) return null;

  const cards = [
    { label: "Værktøjer i alt", value: stats.tools_total, color: "text-gray-900" },
    { label: "Tilgængelige", value: stats.tools_available, color: "text-green-700" },
    { label: "Udlånte", value: stats.tools_lent, color: "text-blue-700" },
    { label: "Til vedligehold", value: stats.tools_maintenance, color: "text-yellow-700" },
    { label: "Materialer", value: stats.materials_count, color: "text-purple-700" },
    { label: "Aktive bookinger", value: stats.bookings_active, color: "text-green-700" },
    { label: "Forfaldne", value: stats.bookings_overdue, color: "text-red-700" },
    { label: "Afventer godkendelse", value: stats.submissions_pending, color: "text-orange-700" },
    { label: "Planlagt vedligehold", value: stats.maintenance_scheduled, color: "text-yellow-700" },
    { label: "RFID brugere", value: stats.rfid_users, color: "text-gray-700" },
    { label: "Controllere", value: stats.controllers, color: "text-gray-700" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">{c.label}</p>
          <p className={`text-3xl font-bold ${c.color}`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Catalog tab
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_TOOL = {
  name: "",
  category: "",
  item_type: "tool",
  description: "",
  brand: "",
  model: "",
  serial_number: "",
  barcode: "",
  condition: "good",
  status: "available",
  requires_training: false,
  daily_rate: 0,
  subscription_included: true,
  is_free: false,
  tags: "",
  image_url: "",
  notes: "",
};

function CatalogTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", item_type: "", category: "" });
  const [modal, setModal] = useState<"create" | "edit" | "storage" | null>(null);
  const [editTool, setEditTool] = useState<Partial<typeof EMPTY_TOOL>>({});
  const [editUuid, setEditUuid] = useState("");
  const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
  const [subGroupId, setSubGroupId] = useState("");
  const [storTool, setStorTool] = useState<Tool | null>(null);
  const [newLoc, setNewLoc] = useState<Partial<StorageLocation>>({ type: "shelf", label: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api("tools", token)
      .then((d) => {
        const resp = d as { tools?: Tool[] } | Tool[];
        setTools(Array.isArray(resp) ? resp : (resp as { tools?: Tool[] }).tools ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    api("substitution-groups", token)
      .then((d) => setSubGroups(d as SubGroup[]))
      .catch(() => {});
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const filtered = tools.filter((t) => {
    if (filter.status && t.status !== filter.status) return false;
    if (filter.item_type && t.item_type !== filter.item_type) return false;
    if (filter.category && !t.category.toLowerCase().includes(filter.category.toLowerCase())) return false;
    return true;
  });

  const openCreate = () => {
    setEditTool({ ...EMPTY_TOOL });
    setSubGroupId("");
    setModal("create");
  };

  const openEdit = (t: Tool) => {
    setEditUuid(t.uuid);
    setEditTool({
      name: t.name,
      category: t.category,
      item_type: t.item_type,
      description: t.description || "",
      brand: t.brand || "",
      model: t.model || "",
      serial_number: t.serial_number || "",
      barcode: t.barcode || "",
      condition: t.condition,
      status: t.status,
      requires_training: t.requires_training ?? false,
      daily_rate: t.daily_rate,
      subscription_included: t.subscription_included ?? true,
      is_free: t.is_free ?? false,
      tags: (t.tags || []).join(", "),
      image_url: t.image_url || "",
      notes: "",
    });
    setSubGroupId(t.substitution_group_id || "");
    setModal("edit");
  };

  const saveTool = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editTool,
        tags: String(editTool.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        daily_rate: Number(editTool.daily_rate || 0),
      };
      if (modal === "create") {
        const created = (await api("tools", token, {
          method: "POST",
          body: JSON.stringify(payload),
        })) as Tool;
        if (subGroupId) {
          await api(`tools/${created.uuid}/assign-group`, token, {
            method: "POST",
            body: JSON.stringify({ group_id: subGroupId }),
          });
        }
      } else {
        await api(`tools/${editUuid}`, token, { method: "PUT", body: JSON.stringify(payload) });
        if (subGroupId) {
          await api(`tools/${editUuid}/assign-group`, token, {
            method: "POST",
            body: JSON.stringify({ group_id: subGroupId }),
          });
        }
      }
      setModal(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteTool = async (uuid: string, name: string) => {
    if (!confirm(`Slet "${name}"?`)) return;
    try {
      await api(`tools/${uuid}`, token, { method: "DELETE" });
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const addLocation = async () => {
    if (!storTool || !newLoc.label) return;
    setSaving(true);
    try {
      await api(`tools/${storTool.uuid}/storage-locations`, token, {
        method: "POST",
        body: JSON.stringify(newLoc),
      });
      setNewLoc({ type: "shelf", label: "" });
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const removeLocation = async (uuid: string, label: string) => {
    try {
      await api(`tools/${uuid}/storage-locations/${encodeURIComponent(label)}`, token, { method: "DELETE" });
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters + create */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          className="border rounded-lg px-3 py-1.5 text-sm"
          placeholder="Kategori..."
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
        />
        <select
          className="border rounded-lg px-3 py-1.5 text-sm"
          value={filter.item_type}
          onChange={(e) => setFilter((f) => ({ ...f, item_type: e.target.value }))}
        >
          <option value="">Alle typer</option>
          <option value="tool">Værktøj</option>
          <option value="material">Materiale</option>
        </select>
        <select
          className="border rounded-lg px-3 py-1.5 text-sm"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">Alle statusser</option>
          <option value="available">Tilgængelig</option>
          <option value="lent">Udlånt</option>
          <option value="maintenance">Vedligehold</option>
          <option value="lost">Mistet</option>
        </select>
        <div className="ml-auto flex gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            🔄 Opdater
          </button>
          <button
            onClick={openCreate}
            className="px-3 py-1.5 text-white rounded-lg text-sm font-medium"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            + Tilføj
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Navn</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Kategori</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Pris/dag</th>
                <th className="px-3 py-2 text-left">Stand</th>
                <th className="px-3 py-2 text-left">Lager</th>
                <th className="px-3 py-2 text-left">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((t) => (
                <tr key={t.uuid} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">
                    {t.name}
                    {t.is_free && <span className="ml-1 text-xs text-green-600">(gratis)</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{t.item_type}</td>
                  <td className="px-3 py-2 text-gray-500">{t.category}</td>
                  <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                  <td className="px-3 py-2">{t.daily_rate > 0 ? `${t.daily_rate} kr` : "—"}</td>
                  <td className="px-3 py-2 text-gray-500">{t.condition}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {(t.storage_locations || []).map((l) => l.label).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEdit(t)}
                        className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => { setStorTool(t); setModal("storage"); }}
                        className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                      >
                        📍
                      </button>
                      <button
                        onClick={() => deleteTool(t.uuid, t.name)}
                        className="px-2 py-0.5 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    Ingen elementer fundet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {(modal === "create" || modal === "edit") && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4">
                {modal === "create" ? "Tilføj element" : "Rediger element"}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Navn *", "name", "text"],
                  ["Kategori *", "category", "text"],
                  ["Mærke", "brand", "text"],
                  ["Model", "model", "text"],
                  ["Serienummer", "serial_number", "text"],
                  ["Stregkode", "barcode", "text"],
                  ["Pris pr. dag (kr)", "daily_rate", "number"],
                  ["Billede URL", "image_url", "text"],
                  ["Tags (komma-separeret)", "tags", "text"],
                ].map(([label, key, type]) => (
                  <div key={key} className={key === "tags" || key === "image_url" ? "col-span-2" : ""}>
                    <label className="text-xs text-gray-600 mb-1 block">{label}</label>
                    <input
                      type={type}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm"
                      value={String((editTool as Record<string, unknown>)[key] ?? "")}
                      onChange={(e) =>
                        setEditTool((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="text-xs text-gray-600 mb-1 block">Beskrivelse</label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                    rows={3}
                    value={editTool.description || ""}
                    onChange={(e) => setEditTool((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Type</label>
                  <select
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                    value={editTool.item_type || "tool"}
                    onChange={(e) => setEditTool((p) => ({ ...p, item_type: e.target.value }))}
                  >
                    <option value="tool">Værktøj</option>
                    <option value="material">Materiale</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Stand</label>
                  <select
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                    value={editTool.condition || "good"}
                    onChange={(e) => setEditTool((p) => ({ ...p, condition: e.target.value }))}
                  >
                    <option value="new">Ny</option>
                    <option value="good">God</option>
                    <option value="fair">Acceptabel</option>
                    <option value="poor">Dårlig</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Status</label>
                  <select
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                    value={editTool.status || "available"}
                    onChange={(e) => setEditTool((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="available">Tilgængelig</option>
                    <option value="lent">Udlånt</option>
                    <option value="maintenance">Vedligehold</option>
                    <option value="lost">Mistet</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Substitutionsgruppe</label>
                  <select
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                    value={subGroupId}
                    onChange={(e) => setSubGroupId(e.target.value)}
                  >
                    <option value="">— ingen —</option>
                    {subGroups.map((g) => (
                      <option key={g.group_id} value={g.group_id}>
                        {g.name} ({g.category})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 flex gap-6 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editTool.is_free}
                      onChange={(e) => setEditTool((p) => ({ ...p, is_free: e.target.checked }))}
                    />
                    Gratis udlån
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editTool.requires_training}
                      onChange={(e) =>
                        setEditTool((p) => ({ ...p, requires_training: e.target.checked }))
                      }
                    />
                    Kræver oplæring
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editTool.subscription_included}
                      onChange={(e) =>
                        setEditTool((p) => ({ ...p, subscription_included: e.target.checked }))
                      }
                    />
                    Inkluderet i abonnement
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6 justify-end">
                <button
                  onClick={() => setModal(null)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuller
                </button>
                <button
                  onClick={saveTool}
                  disabled={saving || !editTool.name}
                  className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: "#2d6a4f" }}
                >
                  {saving ? "Gemmer..." : "Gem"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Storage locations modal */}
      {modal === "storage" && storTool && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-bold">📍 Placeringer — {storTool.name}</h2>

            {/* Existing locations */}
            <div className="space-y-2">
              {(tools.find((t) => t.uuid === storTool.uuid)?.storage_locations || []).map((loc) => (
                <div key={loc.label} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium">{loc.label}</span>{" "}
                    <span className="text-gray-500">({loc.type})</span>
                    {loc.shelf_id && ` – Hylde: ${loc.shelf_id}`}
                    {loc.section && ` / ${loc.section}`}
                    {loc.address && ` – ${loc.address}`}
                  </span>
                  <button
                    onClick={() => removeLocation(storTool.uuid, loc.label)}
                    className="text-red-500 text-xs hover:underline ml-3"
                  >
                    Fjern
                  </button>
                </div>
              ))}
              {!(tools.find((t) => t.uuid === storTool.uuid)?.storage_locations || []).length && (
                <p className="text-sm text-gray-400">Ingen placeringer endnu</p>
              )}
            </div>

            {/* Add new location */}
            <div className="border-t pt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">Tilføj placering</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Type</label>
                  <select
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                    value={newLoc.type || "shelf"}
                    onChange={(e) => setNewLoc((p) => ({ ...p, type: e.target.value }))}
                  >
                    <option value="locker">Skab</option>
                    <option value="shelf">Hylde</option>
                    <option value="address">Adresse</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Label *</label>
                  <input
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                    placeholder="f.eks. Hylde B3"
                    value={newLoc.label || ""}
                    onChange={(e) => setNewLoc((p) => ({ ...p, label: e.target.value }))}
                  />
                </div>
                {newLoc.type === "shelf" && (
                  <>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Hylde ID</label>
                      <input
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.shelf_id || ""}
                        onChange={(e) => setNewLoc((p) => ({ ...p, shelf_id: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Sektion</label>
                      <input
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.section || ""}
                        onChange={(e) => setNewLoc((p) => ({ ...p, section: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                {newLoc.type === "locker" && (
                  <>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Controller ID</label>
                      <input
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.controller_id || ""}
                        onChange={(e) => setNewLoc((p) => ({ ...p, controller_id: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Skab nr.</label>
                      <input
                        type="number"
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.locker_number || ""}
                        onChange={(e) =>
                          setNewLoc((p) => ({ ...p, locker_number: Number(e.target.value) }))
                        }
                      />
                    </div>
                  </>
                )}
                {newLoc.type === "address" && (
                  <>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">Adresse</label>
                      <input
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.address || ""}
                        onChange={(e) => setNewLoc((p) => ({ ...p, address: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Rum</label>
                      <input
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={newLoc.room || ""}
                        onChange={(e) => setNewLoc((p) => ({ ...p, room: e.target.value }))}
                      />
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={addLocation}
                disabled={saving || !newLoc.label}
                className="px-4 py-1.5 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                {saving ? "Tilføjer..." : "+ Tilføj placering"}
              </button>
            </div>

            <div className="flex justify-end mt-2">
              <button
                onClick={() => setModal(null)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bookings tab
// ─────────────────────────────────────────────────────────────────────────────

function BookingsTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api("bookings", token)
      .then((d) => {
        const resp = d as { bookings?: Booking[] } | Booking[];
        setBookings(Array.isArray(resp) ? resp : (resp as { bookings?: Booking[] }).bookings ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const doReturn = async (id: string) => {
    try {
      await api(`bookings/${id}/return`, token, { method: "POST", body: JSON.stringify({}) });
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const filtered = bookings.filter((b) => !statusFilter || b.status === statusFilter);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <select
          className="border rounded-lg px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Alle statusser</option>
          <option value="active">Aktive</option>
          <option value="overdue">Forfaldne</option>
          <option value="returned">Returneret</option>
        </select>
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">
          🔄 Opdater
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Bruger</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Start</th>
                <th className="px-3 py-2 text-left">Slut</th>
                <th className="px-3 py-2 text-left">Formål</th>
                <th className="px-3 py-2 text-left">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((b) => (
                <tr key={b.booking_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{b.user_name}</td>
                  <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                  <td className="px-3 py-2 text-gray-500">
                    {new Date(b.start_date).toLocaleDateString("da-DK")}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {new Date(b.end_date).toLocaleDateString("da-DK")}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{b.billing_type}</td>
                  <td className="px-3 py-2">
                    {(b.status === "active" || b.status === "overdue") && (
                      <button
                        onClick={() => doReturn(b.booking_id)}
                        className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                      >
                        ↩ Returner
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    Ingen bookinger
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Maintenance tab
// ─────────────────────────────────────────────────────────────────────────────

function MaintenanceTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [parts, setParts] = useState<PartEntry[]>([{ part_name: "", part_number: "", quantity: 1, unit_price: 0 }]);
  const [form, setForm] = useState({
    tool_uuid: "",
    maintenance_type: "inspection",
    performed_by: "",
    description: "",
    scheduled_date: "",
    status: "scheduled",
    labour_hours: 0,
    labour_rate: 0,
    notes: "",
  });
  const [billing, setBilling] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api("maintenance", token).then((d) => setLogs(d as MaintenanceLog[])),
      api("tools", token).then((d) => {
        const resp = d as { tools?: Tool[] } | Tool[];
        setTools(Array.isArray(resp) ? resp : (resp as { tools?: Tool[] }).tools ?? []);
      }),
    ])
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const submitLog = async () => {
    try {
      await api("maintenance", token, {
        method: "POST",
        body: JSON.stringify({ ...form, parts_used: parts }),
      });
      setModal(false);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const billParts = async (log_id: string) => {
    setBilling((p) => ({ ...p, [log_id]: true }));
    try {
      const result = (await api(`maintenance/${log_id}/bill-parts`, token, { method: "POST" })) as {
        invoice_number: string;
        total: number;
      };
      alert(`Faktura oprettet: ${result.invoice_number} — ${result.total} kr`);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBilling((p) => ({ ...p, [log_id]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-end">
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">
          🔄 Opdater
        </button>
        <button
          onClick={() => setModal(true)}
          className="px-3 py-1.5 text-white rounded-lg text-sm font-medium"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          + Ny log
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Værktøj</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Planlagt</th>
                <th className="px-3 py-2 text-left">Total</th>
                <th className="px-3 py-2 text-left">Faktura</th>
                <th className="px-3 py-2 text-left">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((l) => (
                <tr key={l.log_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{l.tool_name}</td>
                  <td className="px-3 py-2 text-gray-500">{l.maintenance_type}</td>
                  <td className="px-3 py-2"><StatusBadge status={l.status} /></td>
                  <td className="px-3 py-2 text-gray-500">
                    {l.scheduled_date
                      ? new Date(l.scheduled_date).toLocaleDateString("da-DK")
                      : "—"}
                  </td>
                  <td className="px-3 py-2">{l.total_cost > 0 ? `${l.total_cost} kr` : "—"}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {l.odoo_invoice_number || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {!l.odoo_invoice_number && l.parts_used?.length > 0 && (
                      <button
                        onClick={() => billParts(l.log_id)}
                        disabled={billing[l.log_id]}
                        className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        {billing[l.log_id] ? "..." : "💳 Fakturér"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    Ingen vedligeholds-logs
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* New log modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-bold">Ny vedligeholds-log</h2>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Værktøj *</label>
                <select
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.tool_uuid}
                  onChange={(e) => setForm((p) => ({ ...p, tool_uuid: e.target.value }))}
                >
                  <option value="">Vælg...</option>
                  {tools.map((t) => (
                    <option key={t.uuid} value={t.uuid}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Type</label>
                <select
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.maintenance_type}
                  onChange={(e) => setForm((p) => ({ ...p, maintenance_type: e.target.value }))}
                >
                  <option value="inspection">Inspektion</option>
                  <option value="repair">Reparation</option>
                  <option value="cleaning">Rengøring</option>
                  <option value="calibration">Kalibrering</option>
                  <option value="spare_parts">Reservedele</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Status</label>
                <select
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="scheduled">Planlagt</option>
                  <option value="in_progress">I gang</option>
                  <option value="completed">Afsluttet</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Planlagt dato</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.scheduled_date}
                  onChange={(e) => setForm((p) => ({ ...p, scheduled_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Udført af</label>
                <input
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.performed_by}
                  onChange={(e) => setForm((p) => ({ ...p, performed_by: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Arbejdstimer</label>
                <input
                  type="number"
                  step="0.5"
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.labour_hours}
                  onChange={(e) => setForm((p) => ({ ...p, labour_hours: Number(e.target.value) }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Timepris (kr)</label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  value={form.labour_rate}
                  onChange={(e) => setForm((p) => ({ ...p, labour_rate: Number(e.target.value) }))}
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 block mb-1">Beskrivelse</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Parts */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Reservedele</p>
              {parts.map((pt, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                  <input
                    className="col-span-2 border rounded-lg px-2 py-1.5 text-sm"
                    placeholder="Navn"
                    value={pt.part_name}
                    onChange={(e) =>
                      setParts((prev) =>
                        prev.map((p, j) => (j === i ? { ...p, part_name: e.target.value } : p))
                      )
                    }
                  />
                  <input
                    type="number"
                    className="border rounded-lg px-2 py-1.5 text-sm"
                    placeholder="Antal"
                    value={pt.quantity}
                    onChange={(e) =>
                      setParts((prev) =>
                        prev.map((p, j) => (j === i ? { ...p, quantity: Number(e.target.value) } : p))
                      )
                    }
                  />
                  <input
                    type="number"
                    className="border rounded-lg px-2 py-1.5 text-sm"
                    placeholder="Pris/stk"
                    value={pt.unit_price}
                    onChange={(e) =>
                      setParts((prev) =>
                        prev.map((p, j) => (j === i ? { ...p, unit_price: Number(e.target.value) } : p))
                      )
                    }
                  />
                </div>
              ))}
              <button
                onClick={() =>
                  setParts((p) => [...p, { part_name: "", part_number: "", quantity: 1, unit_price: 0 }])
                }
                className="text-sm text-green-700 hover:underline"
              >
                + Tilføj reservedel
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModal(false)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Annuller
              </button>
              <button
                onClick={submitLog}
                disabled={!form.tool_uuid}
                className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                Gem log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QR pickup card (shown inside each community-pickup submission)
// ─────────────────────────────────────────────────────────────────────────────

function QrPickupCard({ submission: s, token }: { submission: Submission; token: string }) {
  const [qrB64, setQrB64] = useState(s.pickup_qr_b64 ?? "");
  const [pickupUrl, setPickupUrl] = useState(s.pickup_url ?? "");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateQr = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`/api/toollib/submissions/${s.submission_id}/qr`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setQrB64(json.pickup_qr_b64 ?? "");
      setPickupUrl(json.pickup_url ?? "");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    if (pickupUrl) { navigator.clipboard.writeText(pickupUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
  };

  if (!qrB64) {
    return (
      <div className="mt-2">
        <button
          onClick={generateQr}
          disabled={generating}
          className="text-xs px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
        >
          {generating ? "⏳ Genererer QR…" : "🔗 Generer afhentnings-QR"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-2">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={qrB64} alt="QR afhentning" className="w-20 h-20 rounded border border-gray-200 bg-white flex-shrink-0" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs font-semibold text-gray-700">📲 Afhentnings-QR</p>
        {pickupUrl && (
          <p className="text-xs text-gray-500 break-all leading-tight">{pickupUrl}</p>
        )}
        <div className="flex gap-1 flex-wrap">
          <button onClick={copyLink} className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded hover:bg-gray-100">
            {copied ? "✓ Kopieret" : "📋 Kopier link"}
          </button>
          {pickupUrl && (
            <a href={pickupUrl} target="_blank" rel="noreferrer" className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded hover:bg-gray-100">
              🔗 Åbn side
            </a>
          )}
          <button
            onClick={generateQr}
            disabled={generating}
            className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {generating ? "⏳…" : "🔄 Forny QR"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Approved donations management list
// ─────────────────────────────────────────────────────────────────────────────

function ApprovedDonationsList({
  subs,
  token,
  onRefresh,
}: {
  subs: Submission[];
  token: string;
  onRefresh: () => void;
}) {
  const [typeFilter, setTypeFilter] = useState("");
  const [editingNotes, setEditingNotes] = useState<string | null>(null); // submission_id being edited
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);

  const visible = subs.filter((s) => !typeFilter || s.item_type === typeFilter);

  const tools = visible.filter((s) => s.item_type === "tool").length;
  const materials = visible.filter((s) => s.item_type === "material").length;
  const community = visible.filter((s) => s.pickup_token).length;

  const saveNotes = async (submission_id: string) => {
    setSavingNotes(true);
    try {
      await api(`submissions/${submission_id}/notes`, token, {
        method: "PATCH",
        body: JSON.stringify({ admin_notes: notesDraft }),
      });
      setEditingNotes(null);
      onRefresh();
    } finally {
      setSavingNotes(false);
    }
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1800);
  };

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const conditionColor = (c: string) => {
    if (c === "god" || c === "ny") return "bg-green-100 text-green-800";
    if (c === "brugt") return "bg-yellow-100 text-yellow-800";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
        <span className="text-sm font-semibold text-green-900">✅ Godkendte donationer</span>
        <span className="text-xs text-green-700 bg-white border border-green-200 rounded-full px-2 py-0.5">{visible.length} i alt</span>
        {tools > 0 && <span className="text-xs text-blue-700 bg-white border border-blue-200 rounded-full px-2 py-0.5">🔧 {tools} værktøj</span>}
        {materials > 0 && <span className="text-xs text-purple-700 bg-white border border-purple-200 rounded-full px-2 py-0.5">📦 {materials} materialer</span>}
        {community > 0 && <span className="text-xs text-orange-700 bg-white border border-orange-200 rounded-full px-2 py-0.5">📲 {community} m. QR afhentning</span>}
        <div className="ml-auto">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-xs border rounded-lg px-2 py-1"
          >
            <option value="">Alle typer</option>
            <option value="tool">Kun værktøj</option>
            <option value="material">Kun materialer</option>
          </select>
        </div>
      </div>

      {visible.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
          Ingen godkendte donationer
        </div>
      )}

      {visible.map((s) => {
        const isExpanded = expanded.has(s.submission_id);
        const isEditingNotes = editingNotes === s.submission_id;
        const approvedDate = s.reviewed_at
          ? new Date(s.reviewed_at).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" })
          : s.created_at
          ? new Date(s.created_at).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" })
          : "–";

        return (
          <div key={s.submission_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Main row */}
            <div className="flex gap-3 p-4">
              {/* Thumbnail */}
              <div className="flex-shrink-0">
                {s.image_url ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img src={s.image_url} alt={s.item_name} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-2xl">
                    {s.item_type === "tool" ? "🔧" : "📦"}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{s.item_name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${conditionColor(s.condition)}`}>{s.condition}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">{s.category}</span>
                  {s.status === "archived" && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-500">arkiveret</span>
                  )}
                </div>

                {/* Donor row */}
                <p className="text-xs text-gray-500">
                  👤 {s.submitter_name}
                  {s.submitter_email && <span> · <a href={`mailto:${s.submitter_email}`} className="text-blue-600 hover:underline">{s.submitter_email}</a></span>}
                  {s.submitter_phone && <span> · 📞 {s.submitter_phone}</span>}
                </p>

                {/* Tool UUID + catalog link */}
                {s.tool_uuid && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-gray-400">Katalog-ID:</span>
                    <code className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded font-mono">{s.tool_uuid.slice(0, 8)}…</code>
                    <button
                      onClick={() => copyText(s.tool_uuid!, s.tool_uuid!)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {copied === s.tool_uuid ? "✓ kopieret" : "📋 kopier"}
                    </button>
                  </div>
                )}

                {/* Pickup type */}
                <div className="flex items-center gap-2 flex-wrap">
                  {s.pickup_preference === "pickup" ? (
                    <span className="text-xs text-blue-700">🚐 Lokal afhentning</span>
                  ) : s.pickup_preference === "apoteket" ? (
                    <span className="text-xs text-green-700">🏥 Afleveres til Apoteket</span>
                  ) : (
                    <span className="text-xs text-gray-500">🚶 Leveres af donor</span>
                  )}
                  <span className="text-xs text-gray-400">· Godkendt {approvedDate}</span>
                </div>

                {/* Admin notes — inline editable */}
                <div>
                  {isEditingNotes ? (
                    <div className="flex gap-2 items-end mt-1">
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        rows={2}
                        className="flex-1 text-xs border rounded-lg px-2 py-1 resize-none focus:outline-none"
                        placeholder="Admin-noter…"
                      />
                      <button
                        onClick={() => saveNotes(s.submission_id)}
                        disabled={savingNotes}
                        className="text-xs px-2 py-1 text-white rounded-lg disabled:opacity-50"
                        style={{ backgroundColor: "#2d6a4f" }}
                      >
                        {savingNotes ? "…" : "Gem"}
                      </button>
                      <button onClick={() => setEditingNotes(null)} className="text-xs px-2 py-1 border rounded-lg hover:bg-gray-50">
                        Annuller
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 mt-0.5">
                      {s.admin_notes ? (
                        <p className="text-xs text-orange-700 flex-1">📝 {s.admin_notes}</p>
                      ) : (
                        <p className="text-xs text-gray-300 flex-1 italic">Ingen admin-noter</p>
                      )}
                      <button
                        onClick={() => { setEditingNotes(s.submission_id); setNotesDraft(s.admin_notes ?? ""); }}
                        className="text-xs text-gray-400 hover:text-gray-700 flex-shrink-0"
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Expand toggle */}
              <button
                onClick={() => toggleExpand(s.submission_id)}
                className="self-start text-gray-400 hover:text-gray-700 text-xs px-2 py-1 border rounded-lg hover:bg-gray-50 flex-shrink-0"
              >
                {isExpanded ? "▲" : "▼"}
              </button>
            </div>

            {/* Expanded detail panel */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
                {s.description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-0.5">Beskrivelse</p>
                    <p className="text-sm text-gray-700">{s.description}</p>
                  </div>
                )}
                {(s.pickup_address || s.pickup_time_span || s.pickup_instructions) && (
                  <div className="grid grid-cols-1 gap-1.5">
                    <p className="text-xs font-semibold text-gray-600">Afhentningsoplysninger</p>
                    {s.pickup_address && <p className="text-xs text-gray-700">📍 {s.pickup_address}</p>}
                    {s.pickup_time_span && <p className="text-xs text-gray-700">🕐 {s.pickup_time_span}</p>}
                    {s.pickup_instructions && <p className="text-xs text-gray-700">📋 {s.pickup_instructions}</p>}
                  </div>
                )}
                {s.pickup_token && (
                  <QrPickupCard submission={s} token={token} />
                )}
                {s.tool_uuid && (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => copyText(s.tool_uuid!, `full-${s.tool_uuid}`)}
                      className="text-xs px-2 py-1 border rounded-lg hover:bg-white"
                    >
                      {copied === `full-${s.tool_uuid}` ? "✓ UUID kopieret" : "📋 Kopier fuldt UUID"}
                    </button>
                    <a
                      href={`/api/toollib/tools/${s.tool_uuid}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-2 py-1 border rounded-lg hover:bg-white text-blue-600"
                    >
                      🔗 Vis i API
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Submissions tab
// ─────────────────────────────────────────────────────────────────────────────

function SubmissionsTab({ token, setError, setTab }: { token: string; setError: (e: string) => void; setTab: (t: AdminTab) => void }) {
  const [subs, setSubs] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [reviewing, setReviewing] = useState<Submission | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  void setTab; // available for future use

  const load = useCallback(() => {
    setLoading(true);
    api(`submissions${filter ? `?status=${filter}` : ""}`, token)
      .then((d) => setSubs(d as Submission[]))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, filter, setError]);

  useEffect(() => { load(); }, [load]);

  const review = async (status: "approved" | "rejected") => {
    if (!reviewing) return;
    setSaving(true);
    try {
      await api(`submissions/${reviewing.submission_id}/review`, token, {
        method: "PUT",
        body: JSON.stringify({ status, admin_notes: notes, reviewed_by: "admin" }),
      });
      setReviewing(null);
      setNotes("");
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <select
          className="border rounded-lg px-3 py-1.5 text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">Alle</option>
          <option value="pending">Afventer</option>
          <option value="approved">Godkendt</option>
          <option value="rejected">Afvist</option>
        </select>
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">
          🔄 Opdater
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : filter === "approved" ? (
        <ApprovedDonationsList subs={subs} token={token} onRefresh={load} />
      ) : (
        <div className="grid gap-3">
          {subs.map((s) => (
            <div
              key={s.submission_id}
              className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4"
            >
              {s.image_url && (
                <img
                  src={s.image_url}
                  alt={s.item_name}
                  className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{s.item_name}</p>
                    <p className="text-xs text-gray-500">
                      {s.item_type} · {s.category} · Stand: {s.condition}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Fra: {s.submitter_name}{s.submitter_email && ` (${s.submitter_email})`}{s.submitter_phone && ` · 📞 ${s.submitter_phone}`}
                    </p>
                    {s.pickup_preference && (
                      <p className="text-xs mt-1">
                        {s.pickup_preference === "pickup" ? (
                          <span className="text-blue-700">🚐 <strong>Afhentning</strong>{s.storage_preference ? `: ${s.storage_preference}` : ""}</span>
                        ) : (
                          <span className="text-green-700">🚶 <strong>Leveres</strong> til Materialebiblioteket</span>
                        )}
                      </p>
                    )}
                    {s.pickup_address && (
                      <p className="text-xs text-blue-700 mt-0.5">📍 {s.pickup_address}{s.pickup_time_span && ` · ${s.pickup_time_span}`}</p>
                    )}
                    {/* QR code for community pickup */}
                    {s.pickup_token && (
                      <QrPickupCard submission={s} token={token} />
                    )}
                    {s.description && (
                      <p className="text-sm text-gray-600 mt-1">{s.description}</p>
                    )}
                    {s.admin_notes && (
                      <p className="text-xs text-orange-600 mt-1">Admin: {s.admin_notes}</p>
                    )}
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                {s.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => { setReviewing(s); setNotes(""); }}
                      className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Behandle
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {subs.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              Ingen indsendelser
            </div>
          )}
        </div>
      )}

      {/* Review modal */}
      {reviewing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-bold">Behandle: {reviewing.item_name}</h2>
            <div>
              <label className="text-xs text-gray-600 block mb-1">Admin-noter</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Valgfrit..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => review("approved")}
                disabled={saving}
                className="flex-1 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                {saving ? "..." : "✅ Godkend & opret"}
              </button>
              <button
                onClick={() => review("rejected")}
                disabled={saving}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-red-700"
              >
                {saving ? "..." : "❌ Afvis"}
              </button>
            </div>
            <button
              onClick={() => setReviewing(null)}
              className="w-full py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Annuller
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RFID users tab
// ─────────────────────────────────────────────────────────────────────────────

function RfidTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [users, setUsers] = useState<RfidUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api("rfid-users", token)
      .then((d) => {
        const resp = d as { users?: RfidUser[] } | RfidUser[];
        setUsers(Array.isArray(resp) ? resp : (resp as { users?: RfidUser[] }).users ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">
          🔄 Opdater
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Navn</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Adgangsniveau</th>
                <th className="px-3 py-2 text-left">Aktiv</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{u.name}</td>
                  <td className="px-3 py-2 text-gray-500">{u.email}</td>
                  <td className="px-3 py-2 text-gray-500">{u.access_level}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        u.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {u.active ? "Aktiv" : "Inaktiv"}
                    </span>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    Ingen RFID-brugere
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Groups tab
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_GROUP_FORM = {
  name: "",
  description: "",
  pricing_scheme: "per_tool_daily",
  fixed_price: 0,
  is_company: true,
  email: "",
  phone: "",
  street: "",
  street2: "",
  zip_code: "",
  city: "",
  country: "Danmark",
  vat: "",
  website: "",
  tags: "",   // comma-separated string in UI
};

function GroupsTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_GROUP_FORM);
  const [createMsg, setCreateMsg] = useState("");
  const [dayPassGroup, setDayPassGroup] = useState("");
  const [dayPassDate, setDayPassDate] = useState("");
  const [dayPassSaving, setDayPassSaving] = useState(false);
  const [dayPassResult, setDayPassResult] = useState("");
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", pricing_scheme: "per_tool_daily", fixed_price: 0 });
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api("groups", token)
      .then((d) => {
        const resp = d as { groups?: Group[] } | Group[];
        setGroups(Array.isArray(resp) ? resp : (resp as { groups?: Group[] }).groups ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const createGroup = async () => {
    if (!createForm.name.trim()) return;
    setCreating(true);
    setCreateMsg("");
    try {
      const payload = {
        ...createForm,
        tags: createForm.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      const result = await api("groups", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }) as { odoo_partner_id?: number; odoo_partner_error?: string };
      if (result.odoo_partner_error) {
        setCreateMsg(`⚠️ Gruppe oprettet, men Odoo fejlede: ${result.odoo_partner_error}`);
      } else if (result.odoo_partner_id) {
        setCreateMsg(`✅ Gruppe oprettet og koblet til Odoo partner #${result.odoo_partner_id}`);
        setShowCreateModal(false);
        setCreateForm(EMPTY_GROUP_FORM);
      } else {
        setCreateMsg("✅ Gruppe oprettet");
        setShowCreateModal(false);
        setCreateForm(EMPTY_GROUP_FORM);
      }
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (g: Group) => {
    setEditGroup(g);
    setEditForm({
      name: g.name,
      description: g.description || "",
      pricing_scheme: g.pricing_scheme || "per_tool_daily",
      fixed_price: g.fixed_price ?? 0,
    });
  };

  const saveEdit = async () => {
    if (!editGroup) return;
    setEditSaving(true);
    try {
      await api(`groups/${editGroup.group_id}`, token, {
        method: "PUT",
        body: JSON.stringify(editForm),
      });
      setEditGroup(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEditSaving(false);
    }
  };

  const addDayPass = async () => {
    if (!dayPassGroup || !dayPassDate) return;
    setDayPassSaving(true);
    try {
      const result = (await api(`groups/${dayPassGroup}/day-password`, token, {
        method: "POST",
        body: JSON.stringify({ date: dayPassDate }),
      })) as { plain_password: string };
      setDayPassResult(result.plain_password);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDayPassSaving(false);
    }
  };

  const pricingLabel = (g: Group) => {
    if (g.pricing_scheme === "fixed_all") return `Fast pris: ${g.fixed_price ?? 0} kr`;
    return "Per redskab/dag";
  };

  return (
    <div className="space-y-4">
      {/* Edit group modal */}
      {editGroup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Rediger gruppe</h3>

            <div>
              <label className="text-xs font-medium text-gray-600">Gruppenavn</label>
              <input
                className="w-full mt-0.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Beskrivelse</label>
              <input
                className="w-full mt-0.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Valgfri beskrivelse..."
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Prisordning</label>
              <div className="mt-1 flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                <button
                  onClick={() => setEditForm((f) => ({ ...f, pricing_scheme: "per_tool_daily" }))}
                  className={`flex-1 py-2 transition-colors ${editForm.pricing_scheme === "per_tool_daily" ? "bg-green-700 text-white font-medium" : "bg-white text-gray-600"}`}
                >
                  Per redskab/dag
                </button>
                <button
                  onClick={() => setEditForm((f) => ({ ...f, pricing_scheme: "fixed_all" }))}
                  className={`flex-1 py-2 transition-colors ${editForm.pricing_scheme === "fixed_all" ? "bg-green-700 text-white font-medium" : "bg-white text-gray-600"}`}
                >
                  Fast pris (alle)
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {editForm.pricing_scheme === "fixed_all"
                  ? "Alle redskaber dækkes af én fast pris. Materialer faktureres altid pr. forbrug."
                  : "Hvert redskab faktureres separat × antal dage. Materialer pr. forbrug."}
              </p>
            </div>

            {editForm.pricing_scheme === "fixed_all" && (
              <div>
                <label className="text-xs font-medium text-gray-600">Fast pris (kr)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="w-full mt-0.5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  value={editForm.fixed_price}
                  onChange={(e) => setEditForm((f) => ({ ...f, fixed_price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setEditGroup(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Annuller
              </button>
              <button
                onClick={saveEdit}
                disabled={editSaving || !editForm.name.trim()}
                className="flex-1 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                {editSaving ? "Gemmer..." : "Gem ændringer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create group */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Opret ny gruppe</p>
        {createMsg && (
          <div className="mb-2 text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">{createMsg}</div>
        )}
        <button
          onClick={() => { setShowCreateModal(true); setCreateMsg(""); }}
          className="px-4 py-2 text-white rounded-lg text-sm font-medium"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          + Opret ny gruppe
        </button>
      </div>

      {/* ── Create group modal ── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
              <h3 className="font-bold text-gray-900 text-base">Opret ny gruppe / Odoo kunde</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            <div className="overflow-y-auto p-6 space-y-5">
              {/* Person / Virksomhed */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 font-medium w-24">Type</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={!createForm.is_company}
                    onChange={() => setCreateForm((f) => ({ ...f, is_company: false }))}
                    className="accent-green-700"
                  />
                  <span className="text-sm">Person</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={createForm.is_company}
                    onChange={() => setCreateForm((f) => ({ ...f, is_company: true }))}
                    className="accent-green-700"
                  />
                  <span className="text-sm font-medium">Virksomhed</span>
                </label>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {createForm.is_company ? "Virksomhedsnavn" : "Navn"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={createForm.is_company ? "f.eks. Tømmer A/S" : "f.eks. Lars Jensen"}
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-500"
                  autoFocus
                />
              </div>

              {/* Email + Telefon */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">E-mail</label>
                  <input
                    type="email"
                    placeholder="E-mail"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Telefon</label>
                  <input
                    type="tel"
                    placeholder="Telefon"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-500">Adresse</label>
                <input
                  type="text"
                  placeholder="Gade..."
                  value={createForm.street}
                  onChange={(e) => setCreateForm((f) => ({ ...f, street: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                />
                <input
                  type="text"
                  placeholder="Gade 2..."
                  value={createForm.street2}
                  onChange={(e) => setCreateForm((f) => ({ ...f, street2: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    placeholder="Post nr."
                    value={createForm.zip_code}
                    onChange={(e) => setCreateForm((f) => ({ ...f, zip_code: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                  <input
                    type="text"
                    placeholder="By"
                    value={createForm.city}
                    onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                  <input
                    type="text"
                    placeholder="Land"
                    value={createForm.country}
                    onChange={(e) => setCreateForm((f) => ({ ...f, country: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                </div>
              </div>

              {/* Moms + Hjemmeside */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Moms (CVR/VAT)</label>
                  <input
                    type="text"
                    placeholder="f.eks. DK12345678"
                    value={createForm.vat}
                    onChange={(e) => setCreateForm((f) => ({ ...f, vat: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Hjemmeside</label>
                  <input
                    type="url"
                    placeholder="f.eks. https://www.odoo.com"
                    value={createForm.website}
                    onChange={(e) => setCreateForm((f) => ({ ...f, website: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tags</label>
                <input
                  type="text"
                  placeholder='f.eks. "B2B", "VIP", "Konsultation"'
                  value={createForm.tags}
                  onChange={(e) => setCreateForm((f) => ({ ...f, tags: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                />
                <p className="text-xs text-gray-400 mt-1">Adskil med komma. Nye tags oprettes automatisk i Odoo.</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Beskrivelse (intern)</label>
                <textarea
                  placeholder="Intern note om gruppen..."
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400 resize-none"
                />
              </div>

              {createMsg && (
                <div className="text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">{createMsg}</div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
              >
                Annuller
              </button>
              <button
                onClick={createGroup}
                disabled={creating || !createForm.name.trim()}
                className="flex-1 py-2.5 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                {creating ? "⏳ Opretter..." : "Opret gruppe + Odoo kunde"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day password generator */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Generer dags-adgangskode</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Gruppe</label>
            <select
              className="border rounded-lg px-3 py-1.5 text-sm"
              value={dayPassGroup}
              onChange={(e) => setDayPassGroup(e.target.value)}
            >
              <option value="">Vælg gruppe...</option>
              {groups.map((g) => (
                <option key={g.group_id} value={g.group_id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Dato</label>
            <input
              type="date"
              className="border rounded-lg px-3 py-1.5 text-sm"
              value={dayPassDate}
              onChange={(e) => setDayPassDate(e.target.value)}
            />
          </div>
          <button
            onClick={addDayPass}
            disabled={dayPassSaving || !dayPassGroup || !dayPassDate}
            className="px-4 py-1.5 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            {dayPassSaving ? "..." : "Generer"}
          </button>
          {dayPassResult && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-1.5 rounded-lg text-sm font-mono">
              🔑 {dayPassResult}
            </div>
          )}
        </div>
      </div>

      {/* Groups list */}
      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="grid gap-3">
          {groups.map((g) => (
            <div key={g.group_id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{g.name}</p>
                  {g.description && <p className="text-sm text-gray-500">{g.description}</p>}
                  {g.odoo_partner_id ? (
                    <p className="text-xs text-blue-600 mt-0.5">🔗 Odoo partner #{g.odoo_partner_id}</p>
                  ) : (
                    <p className="text-xs text-gray-300 mt-0.5">Ikke koblet til Odoo</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    g.pricing_scheme === "fixed_all"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {pricingLabel(g)}
                  </span>
                  <button
                    onClick={() => openEdit(g)}
                    className="text-xs px-2 py-0.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                  >
                    ✏️ Rediger
                  </button>
                </div>
              </div>
              {g.day_passwords && g.day_passwords.length > 0 ? (
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Dags-adgangskoder</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b">
                        <th className="text-left py-1 pr-4">Dato</th>
                        <th className="text-left py-1 pr-4">Kode</th>
                        <th className="text-left py-1">Gyldige timer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.day_passwords.map((pw) => {
                        const today = new Date().toISOString().slice(0, 10);
                        const isToday = pw.date === today;
                        const isPast = pw.date < today;
                        return (
                          <tr key={pw.date} className={`border-b border-gray-50 ${isToday ? "bg-green-50" : ""}${isPast ? "opacity-50" : ""}`.trim()}>
                            <td className="py-1 pr-4 font-mono">
                              {pw.date}
                              {isToday && <span className="ml-1 text-xs text-green-700 font-semibold">← i dag</span>}
                            </td>
                            <td className="py-1 pr-4">
                              {pw.plain_password ? (
                                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-900 select-all">
                                  {pw.plain_password}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">skjult</span>
                              )}
                            </td>
                            <td className="py-1 text-gray-500 text-xs">
                              Alle tider
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Ingen adgangskoder genereret endnu</p>
              )}
            </div>
          ))}
          {groups.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              Ingen grupper
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Controllers tab
// ─────────────────────────────────────────────────────────────────────────────

function ControllersTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState<Record<string, boolean>>({});
  const [statuses, setStatuses] = useState<Record<string, unknown>>({});

  const load = useCallback(() => {
    setLoading(true);
    api("arduino/controllers", token)
      .then((d) => setControllers(d as Controller[]))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const ping = async (id: string) => {
    setPinging((p) => ({ ...p, [id]: true }));
    try {
      const result = await api(`arduino/controllers/${id}/status`, token);
      setStatuses((p) => ({ ...p, [id]: result }));
    } catch (e: unknown) {
      setStatuses((p) => ({ ...p, [id]: { error: e instanceof Error ? e.message : String(e) } }));
    } finally {
      setPinging((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">
          🔄 Opdater
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">Indlæser...</p>
      ) : (
        <div className="grid gap-3">
          {controllers.map((c) => {
            const status = statuses[c.controller_id] as Record<string, unknown> | undefined;
            return (
              <div key={c.controller_id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{c.controller_id}</p>
                    <p className="text-sm text-gray-500">
                      {c.ip_address} · {c.location} · {c.locker_count} skabe
                    </p>
                    {status && !("error" in status) && (
                      <p className="text-xs text-green-600 mt-1">✅ Online</p>
                    )}
                    {status && "error" in status && (
                      <p className="text-xs text-red-600 mt-1">❌ {String(status.error)}</p>
                    )}
                  </div>
                  <button
                    onClick={() => ping(c.controller_id)}
                    disabled={pinging[c.controller_id]}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {pinging[c.controller_id] ? "⟳" : "📡 Ping"}
                  </button>
                </div>
              </div>
            );
          })}
          {controllers.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              Ingen controllere registreret
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Donation Settings tab — dropoff times + pickup address options
// ─────────────────────────────────────────────────────────────────────────────

interface ScheduleSlot { day: string; from: string; to: string; }
interface DropoffConfig { enabled: boolean; description: string; schedule: ScheduleSlot[]; }
interface CommunityPickup { enabled: boolean; address: string; instructions: string; description: string; time_span: string; }
interface ApoteketPickup { enabled: boolean; instructions: string; description: string; time_span: string; }
interface DonationSettingsData {
  tool_dropoff: DropoffConfig;
  material_dropoff: DropoffConfig;
  pickup_options: { community: CommunityPickup; apoteket: ApoteketPickup; };
}

const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];

const EMPTY_SETTINGS: DonationSettingsData = {
  tool_dropoff: { enabled: true, description: "", schedule: [] },
  material_dropoff: { enabled: true, description: "", schedule: [] },
  pickup_options: {
    community: { enabled: false, address: "", instructions: "", description: "", time_span: "" },
    apoteket: { enabled: true, instructions: "", description: "", time_span: "" },
  },
};

function ScheduleEditor({ schedule, onChange }: { schedule: ScheduleSlot[]; onChange: (s: ScheduleSlot[]) => void }) {
  const add = () => onChange([...schedule, { day: "Mandag", from: "09:00", to: "17:00" }]);
  const remove = (i: number) => onChange(schedule.filter((_, idx) => idx !== i));
  const update = (i: number, field: keyof ScheduleSlot, val: string) =>
    onChange(schedule.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  return (
    <div className="space-y-2">
      {schedule.map((s, i) => (
        <div key={i} className="flex gap-2 items-center">
          <select
            value={s.day}
            onChange={(e) => update(i, "day", e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm flex-1"
          >
            {DAYS.map((d) => <option key={d}>{d}</option>)}
          </select>
          <input type="time" value={s.from} onChange={(e) => update(i, "from", e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm w-24" />
          <span className="text-gray-400 text-sm">–</span>
          <input type="time" value={s.to} onChange={(e) => update(i, "to", e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm w-24" />
          <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">✕</button>
        </div>
      ))}
      <button onClick={add} className="text-sm text-green-700 hover:underline font-medium">+ Tilføj tidslot</button>
    </div>
  );
}

function DropoffSection({ label, config, onChange }: { label: string; config: DropoffConfig; onChange: (c: DropoffConfig) => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={config.enabled}
            onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
            className="rounded" />
          Aktiv
        </label>
      </div>
      <div>
        <label className="text-xs text-gray-600 font-medium block mb-1">Beskrivelse til brugere</label>
        <input value={config.description}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder="Eks: Aflever i vores åbningstid…"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400" />
      </div>
      <div>
        <label className="text-xs text-gray-600 font-medium block mb-2">Åbningstider for aflevering</label>
        <ScheduleEditor schedule={config.schedule} onChange={(s) => onChange({ ...config, schedule: s })} />
      </div>
    </div>
  );
}

function DonationSettingsTab({ token, setError }: { token: string; setError: (e: string) => void }) {
  const [settings, setSettings] = useState<DonationSettingsData>(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api("donation-settings", token)
      .then((d) => setSettings(d as DonationSettingsData))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, setError]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api("donation-settings", token, { method: "PUT", body: JSON.stringify(settings) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const setPickup = <K extends keyof DonationSettingsData["pickup_options"]>(
    key: K, val: DonationSettingsData["pickup_options"][K]
  ) => setSettings((s) => ({ ...s, pickup_options: { ...s.pickup_options, [key]: val } }));

  if (loading) return <p className="text-sm text-gray-500">Indlæser…</p>;

  const { tool_dropoff, material_dropoff, pickup_options } = settings;

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Dropoff times */}
      <DropoffSection
        label="🎁 Donér Værktøj — afleveringstider"
        config={tool_dropoff}
        onChange={(c) => setSettings((s) => ({ ...s, tool_dropoff: c }))}
      />
      <DropoffSection
        label="♻️ Donér Materialer — afleveringstider"
        config={material_dropoff}
        onChange={(c) => setSettings((s) => ({ ...s, material_dropoff: c }))}
      />

      {/* Pickup options */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <h4 className="text-sm font-semibold text-gray-700">🚐 Afhentningsindstillinger</h4>
        <p className="text-xs text-gray-500">
          Donorer kan vælge &quot;I henter&quot; — konfigurer hvilke afhentningstyper der er tilgængelige.
        </p>

        {/* Community pickup */}
        <div className="border border-blue-100 rounded-xl p-3 space-y-3 bg-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">🏘️ Lokal fællesskabsafhentning</span>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={pickup_options.community.enabled}
                onChange={(e) => setPickup("community", { ...pickup_options.community, enabled: e.target.checked })}
                className="rounded" />
              Aktiv
            </label>
          </div>
          <p className="text-xs text-blue-700">Adressen vises offentligt i kataloget med afhentningsinstruktioner.</p>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Afhentningsadresse (vises offentligt)</label>
            <input value={pickup_options.community.address}
              onChange={(e) => setPickup("community", { ...pickup_options.community, address: e.target.value })}
              placeholder="Vejnavn, husnummer, postnummer og by"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Beskrivelse</label>
            <input value={pickup_options.community.description}
              onChange={(e) => setPickup("community", { ...pickup_options.community, description: e.target.value })}
              placeholder="Eks: Lokal afhentning i nærområdet"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Tidsrum</label>
            <input value={pickup_options.community.time_span}
              onChange={(e) => setPickup("community", { ...pickup_options.community, time_span: e.target.value })}
              placeholder="Eks: Hverdage 10:00–18:00"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Afhentningsinstruktioner</label>
            <textarea value={pickup_options.community.instructions}
              onChange={(e) => setPickup("community", { ...pickup_options.community, instructions: e.target.value })}
              placeholder="Eks: Ring på dørklokken, parkering ved siden af bygningen…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none" />
          </div>
        </div>

        {/* Apoteket pickup */}
        <div className="border border-green-100 rounded-xl p-3 space-y-3 bg-green-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-900">🏥 Kun fra Lokalt Innovations hus i Haslev by</span>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={pickup_options.apoteket.enabled}
                onChange={(e) => setPickup("apoteket", { ...pickup_options.apoteket, enabled: e.target.checked })}
                className="rounded" />
              Aktiv
            </label>
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Beskrivelse</label>
            <input value={pickup_options.apoteket.description}
              onChange={(e) => setPickup("apoteket", { ...pickup_options.apoteket, description: e.target.value })}
              placeholder="Eks: Vi afhenter personligt fra din adresse"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400" />
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Tidsrum</label>
            <input value={pickup_options.apoteket.time_span}
              onChange={(e) => setPickup("apoteket", { ...pickup_options.apoteket, time_span: e.target.value })}
              placeholder="Eks: Mandag–Fredag 09:00–16:00"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400" />
          </div>
          <div>
            <label className="text-xs text-gray-600 font-medium block mb-1">Afhentningsinstruktioner</label>
            <textarea value={pickup_options.apoteket.instructions}
              onChange={(e) => setPickup("apoteket", { ...pickup_options.apoteket, instructions: e.target.value })}
              placeholder="Eks: Kontakt os på telefon eller e-mail for at aftale tidspunkt for afhentning…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none" />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 text-sm text-white rounded-xl font-medium disabled:opacity-50"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          {saving ? "Gemmer…" : "💾 Gem indstillinger"}
        </button>
        {saved && <span className="text-sm text-green-600 font-medium">✓ Gemt</span>}
      </div>
    </div>
  );
}
